// src/Reeeg.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext.jsx";
import { ShieldCheck, CheckCircle2, ArrowLeft, AlertTriangle, User, Key, Eye, EyeOff } from "lucide-react";
import SocialAuthButtons from "../SocialAuthButtons.jsx";
import TurnstileWidget, { TURNSTILE_SITE_KEY } from "../TurnstileWidget.jsx";
import { localizeMessage } from '../../utils/toast.js';

// Simple memorable password — no special chars, just Word+Word+Number
const generateSimplePassword = () => {
  const words1 = ["Apple","River","Cloud","Tiger","Stone","Eagle","Ocean","Maple","Flame","Cedar","Frost","Solar","Lunar","Amber","Coral"];
  const words2 = ["Bridge","Forest","Valley","Meadow","Summit","Harbor","Garden","Breeze","Stream","Canyon","Plains","Sunset","Aurora","Crystal","Mirror"];
  const w1 = words1[Math.floor(Math.random() * words1.length)];
  const w2 = words2[Math.floor(Math.random() * words2.length)];
  const num = Math.floor(Math.random() * 900 + 100);
  return `${w1}${w2}${num}`;
};

const generatePasswords = () => Array.from({ length: 6 }, generateSimplePassword);

const checkStrength = (p) => {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[a-z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  return Math.min(s, 5);
};

// Same format the backend enforces (see /api/auth/organizations/register): lowercase
// letters, numbers, underscores and periods only, 3-20 characters — kept in sync so a
// username the UI marks "valid" is never rejected by the server.
const USERNAME_REGEX = /^[a-z0-9_.]{3,20}$/;
const sanitizeUsername = (value) => value.toLowerCase().replace(/[^a-z0-9_.]/g, "");

const generateUsernameSuggestions = (orgName) => {
  if (!orgName) return [];
  const clean = orgName.toLowerCase().replace(/[^a-z0-9]/g, "").substring(0, 12);
  return Array.from(new Set([clean, `${clean}_org`, `${clean}_team`, `official_${clean}`, `${clean}2025`, `${clean}_global`].filter(s => s.length >= 3 && s.length <= 20)));
};

// Persisted on every change so a reload or a back/forward navigation while still on this
// step doesn't lose what's been typed — see the matching load/save effects below.
// Password fields are deliberately excluded: they'd otherwise sit in sessionStorage as
// plaintext for the rest of the tab's life.
const DRAFT_KEY = "orgOnboardingDraftStepFour";
const defaultFormData = {
  username: "", password: "", confirmPassword: "",
  registrantRole: "",
  representative: { name: "", surname: "", position: "", phone: "", email: "" },
};

function Reeeg() {
  const { t } = useTranslation();
  const [stepOneSnapshot, setStepOneSnapshot] = useState(null);
  const [stepTwoSnapshot, setStepTwoSnapshot] = useState(null);
  const [stepThreeSnapshot, setStepThreeSnapshot] = useState(null);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY));
      return saved
        ? { ...defaultFormData, ...saved, password: "", confirmPassword: "" }
        : defaultFormData;
    } catch {
      return defaultFormData;
    }
  });
  const [suggestions, setSuggestions] = useState(generatePasswords());
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [success, setSuccess] = useState("");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);

  const { registerOrganization, socialAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    const { password, confirmPassword, ...rest } = formData;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
  }, [formData]);

  useEffect(() => {
    const s1 = sessionStorage.getItem("orgOnboardingStepOne");
    const s2 = sessionStorage.getItem("orgOnboardingStepTwo");
    const s3 = sessionStorage.getItem("orgOnboardingStepThree");
    if (s1) setStepOneSnapshot(JSON.parse(s1));
    if (s2) setStepTwoSnapshot(JSON.parse(s2));
    if (s3) setStepThreeSnapshot(JSON.parse(s3));
  }, []);

  const update = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const updateRep = (k, v) => setFormData(prev => ({ ...prev, representative: { ...prev.representative, [k]: v } }));

  const usePassword = (pwd) => { update("password", pwd); update("confirmPassword", pwd); setShowPassword(true); setShowConfirm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.username) { setError(t("authPages.orgReg.alertUsernameRequired")); return; }
    if (!USERNAME_REGEX.test(formData.username)) { setError(t("authPages.orgReg.alertUsernameFormat")); return; }
    if (!stepTwoSnapshot?.orgContactEmail) { setError(t("authPages.orgReg.alertMissingEmail")); return; }
    if (!formData.password) { setError(t("authPages.orgReg.alertPasswordRequired")); return; }
    if (formData.password !== formData.confirmPassword) { setError(t("authPages.orgReg.alertPasswordsMismatch")); return; }
    if (formData.password.length < 8) { setError(t("authPages.orgReg.alertPasswordLength")); return; }
    if (!formData.registrantRole) { setError(t("authPages.orgReg.alertRegistrantRole")); return; }
    if (!formData.representative.name.trim()) { setError(t("authPages.orgReg.alertRepName")); return; }
    if (!formData.representative.surname.trim()) { setError(t("authPages.orgReg.alertRepSurname")); return; }
    if (!formData.representative.position.trim()) { setError(t("authPages.orgReg.alertRepPosition")); return; }
    if (TURNSTILE_SITE_KEY && !captchaToken) { setError(t("authPages.orgReg.alertCaptcha")); return; }
    setLoading(true);
    try {
      const result = await registerOrganization({ stepOneData: stepOneSnapshot || {}, stepTwoData: stepTwoSnapshot || {}, stepThreeData: stepThreeSnapshot || {}, stepFourData: { email: stepTwoSnapshot?.orgContactEmail, password: formData.password, username: formData.username, registrantRole: formData.registrantRole, representative: formData.representative }, captchaToken });
      if (result.success) {
        localStorage.setItem("justRegistered", "true");
        localStorage.setItem("showOnboarding", "true");
        sessionStorage.removeItem("orgOnboardingStepOne");
        sessionStorage.removeItem("orgOnboardingStepTwo");
        sessionStorage.removeItem("orgOnboardingStepThree");
        sessionStorage.removeItem("orgOnboardingDraftStepOne");
        sessionStorage.removeItem("orgOnboardingDraftStepTwo");
        sessionStorage.removeItem("orgOnboardingDraftStepThree");
        sessionStorage.removeItem("orgOnboardingDraftStepFour");
        if (result.requiresVerification) {
          setSuccess(t("authPages.orgReg.successEmailVerify"));
          setTimeout(() => navigate("/verify-email", { state: { email: stepTwoSnapshot?.orgContactEmail } }), 400);
        } else {
          setSuccess(t("authPages.orgReg.successRegistered"));
          setTimeout(() => navigate("/home"), 400);
        }
      } else {
        setError(result.error || t("authPages.orgReg.errorRegistrationFailed"));
      }
    } catch (err) {
      setError(err.message || t("authPages.orgReg.errorRegistrationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = async ({ provider, accessToken }) => {
    if (!formData.registrantRole) { setError(t("authPages.orgReg.alertRegistrantRole")); return; }
    if (!formData.representative.name.trim() || !formData.representative.surname.trim() || !formData.representative.position.trim()) {
      setError(t("authPages.orgReg.alertRepName"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await socialAuth({
        provider,
        accessToken,
        mode: "register",
        userType: "organizer",
        organizationData: {
          stepOneData: stepOneSnapshot || {},
          stepTwoData: stepTwoSnapshot || {},
          stepThreeData: stepThreeSnapshot || {},
          stepFourData: {
            registrantRole: formData.registrantRole,
            representative: formData.representative,
          },
        },
        redirectTo: "/home",
      });

      if (result.success) {
        localStorage.setItem("justRegistered", "true");
        localStorage.setItem("showOnboarding", "true");
        sessionStorage.removeItem("orgOnboardingStepOne");
        sessionStorage.removeItem("orgOnboardingStepTwo");
        sessionStorage.removeItem("orgOnboardingStepThree");
        sessionStorage.removeItem("orgOnboardingDraftStepOne");
        sessionStorage.removeItem("orgOnboardingDraftStepTwo");
        sessionStorage.removeItem("orgOnboardingDraftStepThree");
        sessionStorage.removeItem("orgOnboardingDraftStepFour");
        setSuccess(t("authPages.orgReg.successSocial"));
        setTimeout(() => navigate("/home"), 400);
      } else {
        setError(result.error || t("authPages.orgReg.errorSocialFailed"));
      }
    } catch (err) {
      setError(err.message || t("authPages.orgReg.errorSocialFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Shared styles
  const inputStyle = { width: "100%", padding: isMobile ? "10px 12px" : "11px 14px", borderRadius: "10px", border: "1.5px solid #ddd", fontSize: isMobile ? "13px" : "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s", background: "white" };
  const labelStyle = { display: "flex", flexDirection: "column", gap: "5px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#444" };
  const sectionStyle = { background: "white", borderRadius: "14px", padding: isMobile ? "14px 12px" : "20px", border: "1px solid #e9ecef", display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" };
  const sectionTitleStyle = { fontSize: isMobile ? "13px" : "15px", fontWeight: 700, color: "#1a3d2e", margin: 0, display: "flex", alignItems: "center", gap: "6px" };

  const strength = checkStrength(formData.password);
  const strengthColor = strength >= 4 ? "#2e7d32" : strength >= 3 ? "#f59e0b" : "#e53935";
  const strengthLabel = strength >= 4 ? t("authPages.orgReg.strengthStrong") : strength >= 3 ? t("authPages.orgReg.strengthMedium") : t("authPages.orgReg.strengthWeak");

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: "3px solid #e8f0ec", borderTop: "3px solid #32614d", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      <p style={{ color: "#666" }}>{t("authPages.orgReg.submittingRegistration")}</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!stepOneSnapshot) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: 16, padding: 20 }}>
      <AlertTriangle size={40} color="#e53935" />
      <h3 style={{ color: "#1a3d2e", margin: 0 }}>{t("authPages.orgReg.missingInfoTitle")}</h3>
      <p style={{ color: "#888", textAlign: "center" }}>{t("authPages.orgReg.missingInfoDesc")}</p>
      <Link to="/reg" style={{ background: "#32614d", color: "white", padding: "12px 24px", borderRadius: "10px", textDecoration: "none", fontWeight: 700 }}>{t("authPages.orgReg.goToStep1")}</Link>
    </div>
  );

  return (
    <div style={{ background: "#f5f7f0", minHeight: "100vh" }}>
      {/* Progress header */}
      <div style={{ background: "white", borderBottom: "1px solid #e9ecef", padding: isMobile ? "12px 14px" : "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
          {[
            { n: 1, label: isMobile ? t("authPages.orgReg.stepProfileShort") : t("authPages.orgReg.stepProfileLong"), done: true },
            { n: 2, label: isMobile ? t("authPages.orgReg.stepContactShort") : t("authPages.orgReg.stepContactMedium"), done: true },
            { n: 3, label: isMobile ? t("authPages.orgReg.stepTeamShort") : t("authPages.orgReg.stepTeamLong"), done: true },
            { n: 4, label: t("authPages.orgReg.stepVerification"), active: true },
          ].map((step, i) => (
            <React.Fragment key={step.n}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flex: 1 }}>
                <div style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: "50%", background: step.done ? "#c8e6c9" : step.active ? "#32614d" : "#e9ecef", color: step.done ? "#2e7d32" : step.active ? "white" : "#999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? "12px" : "13px", fontWeight: 700 }}>
                  {step.done ? "✓" : step.n}
                </div>
                <span style={{ fontSize: isMobile ? "9px" : "11px", color: step.active ? "#32614d" : step.done ? "#2e7d32" : "#999", fontWeight: step.active ? 700 : 400, textAlign: "center", whiteSpace: "nowrap" }}>{step.label}</span>
              </div>
              {i < 3 && <div style={{ height: "2px", flex: 2, background: step.done ? "#c8e6c9" : "#e9ecef", marginBottom: isMobile ? "16px" : "20px" }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "16px 14px 100px" : "28px 20px 60px" }}>

        {/* Heading + back */}
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: "16px", flexDirection: isMobile ? "column" : "row", gap: "8px" }}>
          <div>
            <h2 style={{ fontSize: isMobile ? "15px" : "20px", fontWeight: 700, color: "#1a3d2e", margin: "0 0 4px" }}>{t("authPages.orgReg.step4Title")}</h2>
            <p style={{ fontSize: isMobile ? "11px" : "13px", color: "#888", margin: 0 }}>{t("authPages.orgReg.step4Subtitle")}</p>
          </div>
          <Link to="/reeg" style={{ display: "flex", alignItems: "center", gap: "6px", color: "#32614d", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, textDecoration: "none", border: "1.5px solid #32614d", borderRadius: "8px", padding: "6px 12px", whiteSpace: "nowrap" }}>
            <ArrowLeft size={14} /> {t("authPages.orgReg.back")}
          </Link>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" }}>

          {/* Login Credentials */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}><Key size={15} /> {t("authPages.orgReg.loginCredentialsTitle")} <span style={{ color: "#e53935" }}>*</span></h3>

            <div style={{ display: "grid", gap: 10 }}>
              <SocialAuthButtons onAuth={handleSocialRegister} disabled={loading} />
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#8a8a8a", fontSize: 11 }}>
                <div style={{ flex: 1, height: 1, background: "#e6ece8" }} />
                <span>{t("authPages.orgReg.orDivider")}</span>
                <div style={{ flex: 1, height: 1, background: "#e6ece8" }} />
              </div>
            </div>

            {/* Username */}
            <label style={labelStyle}>
              <span>{t("authPages.orgReg.username")} <span style={{ color: "#e53935" }}>*</span></span>
              <div style={{ position: "relative" }}>
                <input type="text" value={formData.username} onChange={e => update("username", sanitizeUsername(e.target.value.trim()))}
                  required placeholder={t("authPages.orgReg.usernamePlaceholder")} maxLength={20} style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                <span style={{ position: "absolute", right: 8, bottom: 6, fontSize: "9px", color: "#bbb" }}>{formData.username.length}/20</span>
              </div>
              {formData.username.length > 0 && (
                USERNAME_REGEX.test(formData.username)
                  ? <span style={{ fontSize: "11px", color: "#2e7d32", fontWeight: 600 }}>{t("authPages.orgReg.usernameValid")}</span>
                  : <span style={{ fontSize: "11px", color: "#e53935", fontWeight: 600 }}>{t("authPages.orgReg.usernameInvalid")}</span>
              )}
              {/* Suggestions */}
              {stepOneSnapshot?.nameBlocks?.[0]?.name && (
                <div style={{ marginTop: "4px" }}>
                  <span style={{ fontSize: "10px", color: "#aaa" }}>{t("authPages.orgReg.suggestions")}</span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                    {generateUsernameSuggestions(stepOneSnapshot.nameBlocks[0].name).slice(0, 4).map((s, i) => (
                      <button key={i} type="button" onClick={() => update("username", s)}
                        style={{ padding: "3px 10px", borderRadius: "999px", border: "1px solid #32614d", background: "none", color: "#32614d", fontSize: "11px", cursor: "pointer", fontFamily: "inherit" }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </label>

            {/* Email — set in Step 2, reused here as the account/verification email */}
            <label style={labelStyle}>
              {t("authPages.orgReg.email")}
              <div style={{ ...inputStyle, background: "#f5f7f0", color: "#444", cursor: "default" }}>
                {stepTwoSnapshot?.orgContactEmail || "—"}
              </div>
              <span style={{ fontSize: "11px", color: "#888" }}>
                {t("authPages.orgReg.emailHintPrefix")} <Link to="/regnext" style={{ color: "#32614d", fontWeight: 600 }}>{t("authPages.orgReg.changeIt")}</Link>.
              </span>
            </label>

            {/* Password */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "12px" }}>
              <label style={labelStyle}>
                <span>{t("authPages.orgReg.password")} <span style={{ color: "#e53935" }}>*</span></span>
                <div style={{ position: "relative" }}>
                  <input type={showPassword ? "text" : "password"} value={formData.password}
                    onChange={e => update("password", e.target.value)} required placeholder={t("authPages.orgReg.passwordPlaceholder")}
                    style={{ ...inputStyle, padding: isMobile ? "10px 40px 10px 12px" : "11px 40px 11px 14px" }}
                    onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#888", display: "flex" }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {formData.password && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ display: "flex", gap: "3px", flex: 1 }}>
                      {[1,2,3,4,5].map(i => (
                        <div key={i} style={{ flex: 1, height: "4px", borderRadius: "2px", background: strength >= i ? strengthColor : "#e9ecef", transition: "background 0.2s" }} />
                      ))}
                    </div>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: strengthColor, flexShrink: 0 }}>{strengthLabel}</span>
                  </div>
                )}
              </label>

              <label style={labelStyle}>
                <span>{t("authPages.orgReg.confirmPassword")} <span style={{ color: "#e53935" }}>*</span></span>
                <div style={{ position: "relative" }}>
                  <input type={showConfirm ? "text" : "password"} value={formData.confirmPassword}
                    onChange={e => update("confirmPassword", e.target.value)} required placeholder={t("authPages.orgReg.confirmPasswordPlaceholder")}
                    style={{ ...inputStyle, padding: isMobile ? "10px 40px 10px 12px" : "11px 40px 11px 14px", borderColor: formData.confirmPassword && formData.password !== formData.confirmPassword ? "#e53935" : "#ddd" }}
                    onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = formData.confirmPassword && formData.password !== formData.confirmPassword ? "#e53935" : "#ddd"} />
                  <button type="button" onClick={() => setShowConfirm(p => !p)}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#888", display: "flex" }}>
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {formData.confirmPassword && (
                  <span style={{ fontSize: "11px", fontWeight: 600, color: formData.password === formData.confirmPassword ? "#2e7d32" : "#e53935" }}>
                    {formData.password === formData.confirmPassword ? t("authPages.orgReg.passwordsMatch") : t("authPages.orgReg.passwordsDontMatch")}
                  </span>
                )}
              </label>
            </div>

            {/* Simple password suggestions */}
            <div style={{ background: "#f8faf9", borderRadius: "10px", padding: isMobile ? "10px" : "14px", border: "1px solid #e8f0ec" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: isMobile ? "11px" : "12px", color: "#555", fontWeight: 600 }}>{t("authPages.orgReg.easyPasswordsTitle")}</span>
                <button type="button" onClick={() => setSuggestions(generatePasswords())}
                  style={{ background: "none", border: "1px solid #ddd", borderRadius: "6px", padding: "3px 8px", fontSize: "10px", cursor: "pointer", color: "#666", fontFamily: "inherit" }}>
                  {t("authPages.orgReg.refresh")}
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {suggestions.map((pwd, i) => (
                  <button key={i} type="button" onClick={() => usePassword(pwd)}
                    style={{ padding: "4px 10px", borderRadius: "8px", border: "1px solid #c8dfd2", background: "white", color: "#2a5240", fontSize: isMobile ? "10px" : "11px", cursor: "pointer", fontFamily: "monospace", fontWeight: 600, transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#e8f5e9"}
                    onMouseLeave={e => e.currentTarget.style.background = "white"}>
                    {pwd}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: "10px", color: "#aaa", marginTop: "6px", display: "block" }}>{t("authPages.orgReg.passwordFormatHint")}</span>
            </div>
          </section>

          {/* Who's registering */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}><User size={15} /> {t("authPages.orgReg.legalRepTitle")}</h3>
            <p style={{ margin: 0, fontSize: isMobile ? "11px" : "12px", color: "#888" }}>{t("authPages.orgReg.legalRepDesc")}</p>

            <label style={labelStyle}>
              <span>{t("authPages.orgReg.registrantRoleQuestion")} <span style={{ color: "#e53935" }}>*</span></span>
              <div style={{ display: "flex", gap: "8px" }}>
                {[[t("authPages.orgReg.registrantRoleOrg"), "organization"], [t("authPages.orgReg.registrantRoleRep"), "representative"]].map(([l, val]) => (
                  <button key={val} type="button" onClick={() => update("registrantRole", val)}
                    style={{ flex: 1, padding: isMobile ? "9px 8px" : "10px 12px", borderRadius: "10px", border: `1.5px solid ${formData.registrantRole === val ? "#32614d" : "#ddd"}`, background: formData.registrantRole === val ? "#e8f5e9" : "white", color: formData.registrantRole === val ? "#1a3d2e" : "#555", fontSize: isMobile ? "11px" : "12.5px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    {l}
                  </button>
                ))}
              </div>
            </label>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: "10px" }}>
              <label style={labelStyle}><span>{t("authPages.orgReg.name")} <span style={{ color: "#e53935" }}>*</span></span>
                <input type="text" value={formData.representative.name} onChange={e => updateRep("name", e.target.value)}
                  required placeholder={t("authPages.orgReg.firstNamePlaceholder")} style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </label>
              <label style={labelStyle}><span>{t("authPages.orgReg.surname")} <span style={{ color: "#e53935" }}>*</span></span>
                <input type="text" value={formData.representative.surname} onChange={e => updateRep("surname", e.target.value)}
                  required placeholder={t("authPages.orgReg.lastNamePlaceholder")} style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </label>
              <label style={labelStyle}><span>{t("authPages.orgReg.position")} <span style={{ color: "#e53935" }}>*</span></span>
                <input type="text" value={formData.representative.position} onChange={e => updateRep("position", e.target.value)}
                  required placeholder={t("authPages.orgReg.positionPlaceholder")} style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "10px" }}>
              {[["phone",t("authPages.orgReg.phone"),"tel"],["email",t("authPages.orgReg.email"),"email"]].map(([k,l,ty]) => (
                <label key={k} style={labelStyle}><span>{l} <span style={{ fontSize: "10px", color: "#aaa", fontWeight: 400 }}>{t("authPages.orgReg.optional")}</span></span>
                  <input type={ty} value={formData.representative[k]} onChange={e => updateRep(k, e.target.value)}
                    placeholder={t("authPages.orgReg.optionalPlaceholder")} style={inputStyle}
                    onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                </label>
              ))}
            </div>
          </section>

          {/* Error / Success */}
          {error && (
            <div style={{ background: "#ffebee", color: "#c62828", padding: "12px 14px", borderRadius: "10px", fontSize: "13px", border: "1px solid #ffcdd2", display: "flex", alignItems: "center", gap: "8px" }}>
              <AlertTriangle size={15} /> {localizeMessage(error)}
            </div>
          )}
          {success && (
            <div style={{ background: "#e8f5e9", color: "#2e7d32", padding: "12px 14px", borderRadius: "10px", fontSize: "13px", border: "1px solid #c8e6c9", display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckCircle2 size={15} /> {success}
            </div>
          )}

          <TurnstileWidget onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

          {/* Submit */}
          <button type="submit" disabled={loading || (!!TURNSTILE_SITE_KEY && !captchaToken)}
            style={{ padding: isMobile ? "13px 20px" : "14px 28px", background: (loading || (TURNSTILE_SITE_KEY && !captchaToken)) ? "#ccc" : "linear-gradient(135deg,#32614d,#2a5240)", color: "white", border: "none", borderRadius: "12px", fontSize: isMobile ? "14px" : "15px", fontWeight: 700, cursor: (loading || (TURNSTILE_SITE_KEY && !captchaToken)) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontFamily: "inherit" }}>
            {loading ? t("authPages.orgReg.submittingBtn") : <><ShieldCheck size={16} /> {t("authPages.orgReg.completeRegistration")}</>}
          </button>
        </form>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default Reeeg;
